import { createEntityAdapter, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import client from "../../utils/axios";

const adapter = createEntityAdapter<Group>({
  sortComparer: (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
});
const slice = createSlice({
  name: "groups",
  initialState: adapter.getInitialState({
    status: "idle" as LoadingStatus,
    total: 0,
    pagesCount: 1,
    currentPage: null as number | null,
  }),
  reducers: {
    setStatus(state, { payload }: PayloadAction<LoadingStatus>) {
      state.status = payload;
    },
    hasError(state, action) {
      state.status = "failed";
    },
    getGroupsSuccess(state, action: PayloadAction<ResultList<Group>>) {
      const { meta, data } = action.payload;
      state.status = "success";
      state.total = meta.total;
      state.pagesCount = meta.last_page;
      state.currentPage = meta.current_page;
      adapter.setAll(state, data);
    },
    addGroupSuccess(state, action: PayloadAction<Result<Group>>) {
      state.status = "success";
      adapter.upsertOne(state, action.payload.data);
    },
    deleteGroupSuccess(state, action: PayloadAction<number>) {
      adapter.removeOne(state, action.payload);
    },
  },
});

const { actions, reducer } = slice;

const { selectAll } = adapter.getSelectors();
export const selectGroups = createSelector(
  (state: RootState) => state.groups,
  (groups) => selectAll(groups)
);

export const getGroups: AsyncAction = (page: number) => {
  return async (dispatch, getState) => {
    if (getState().groups.currentPage === page) return;

    dispatch(actions.setStatus("pending"));
    try {
      const res = await client.get<any, ResultList<Group>>("/contact-groups", { params: { page } });
      return dispatch(actions.getGroupsSuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const showGroup: AsyncAction = (id: number) => {
  return async (dispatch, getState) => {
    if (getState().groups.entities[id] !== undefined) return;

    dispatch(actions.setStatus("pending"));
    try {
      const res = await client.get<any, Result<Group>>(`/contact-groups/${id}`);
      return dispatch(actions.addGroupSuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const storeGroup: AsyncAction = (data: StoreGroup) => {
  return async (dispatch) => {
    dispatch(actions.setStatus("mutating"));
    try {
      const res = await client.post<any, Result<Group>>("/contact-groups/", data);
      return dispatch(actions.addGroupSuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const updateGroup: AsyncAction = ({ id, ...patch }: StoreGroup & Pick<Group, "id">) => {
  return async (dispatch) => {
    dispatch(actions.setStatus("mutating"));
    try {
      const res = await client.put<any, Result<Group>>(`/contact-groups/${id}`, patch);
      return dispatch(actions.addGroupSuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const deleteGroup: AsyncAction = (groupId: number) => {
  return async (dispatch, getState) => {
    const item = getState().groups.entities[groupId];
    if (item === undefined) return;

    dispatch(actions.deleteGroupSuccess(groupId));
    try {
      return await client.delete<any, { message: string }>(`/contact-groups/${groupId}`);
    } catch (err) {
      dispatch(actions.addGroupSuccess({ data: item })); // put it pack instead of errors on deleting
      throw dispatch(actions.hasError(err));
    }
  };
};

export default reducer;
